/* eslint-disable @typescript-eslint/no-empty-interface */
import React, { lazy, Suspense, CSSProperties } from 'react';
import {
  AllieLoadingConfig,
  AllieLogoLoading,
  isAppMobile,
  RouteConfigLoading,
} from '@allie/app-ui-lib';
import { loadScript } from './helpers';

// Lazy load EmptyState to avoid styled-components conflicts with Module Federation
const EmptyState = lazy(() => import('@allie/app-ui-lib').then(module => (
  { default: module.EmptyState }
)));

export interface MfeErrorMessage {
  TITLE: string;
  DESCRIPTION_LINE_1: string;
  DESCRIPTION_LINE_2?: string;
  BUTTON_TEXT: string;
}

export interface BaseMFEProps extends CommonAttrs {
  exposedPath: string;
  loadingMessage?: string;
  loadingErrorMessage?: string;
  name: string;
  entryPointUrl: string;
  className?: string;
  configLoading?: AllieLoadingConfig;
  routeConfigLoading?: RouteConfigLoading;
  loadingLogoSize?: number;
  loadingLogoStyle?: CSSProperties;
  errorMessage?: MfeErrorMessage;
  tracker?: any;
  loggedUser?: { id?: number; name?: string; role?: string };
  company?: { name?: string };
  isMobile?: boolean;
  isTablet?: boolean;
}

export type BaseMFEMountParams = CommonAttrs;

export interface BaseMFEMountResponse {
  onParamsChange: any;
}

interface CommonAttrs {}

type MountType<T, R> = (el: Element, params: T) => R;
type UnmountType = (ref: unknown) => void;

interface BaseMFEApp<T, R> {
  mount: MountType<T, R>;
  unmount?: UnmountType;
}

type MyState = {
  isLoading: boolean;
  isError: boolean;
  isRetrying: boolean;
  unmount?: UnmountType;
  update?: any;
};

export abstract class BaseMicroFrontend<T extends BaseMFEProps = any> extends React.Component {
  declare props: T;

  componentRef: React.RefObject<unknown>;
  state: MyState = {
    isLoading: true,
    isError: false,
    isRetrying: false,
  };
  constructor(props: T) {
    super(props);
    this.props = props;
    this.componentRef = React.createRef();
  }

  abstract buildMountParams(): BaseMFEMountParams;
  abstract afterMount(response: BaseMFEMountResponse): void;

  private getTrackingProperties() {
    const { name, loggedUser, company, isMobile, isTablet } = this.props;
    return {
      screen: name,
      user_name: loggedUser?.name,
      user_role: loggedUser?.role,
      company_name: company?.name,
      device: isMobile ? 'mobile' : isTablet ? 'tablet' : 'web',
    };
  }

  componentDidUpdate(_prevProps: T, prevState: MyState) {
    if (this.state.isError && !prevState.isError) {
      this.props.tracker?.track('screen_error_displayed', this.getTrackingProperties());
    }
  }

  afterLoad(app: () => BaseMFEApp<BaseMFEMountParams, BaseMFEMountResponse>) {
    const params = this.buildMountParams();

    let mount: MountType<BaseMFEMountParams, BaseMFEMountResponse>;
    let unmount: UnmountType | undefined;
    
    try {
      const mfeApp = app();
      mount = mfeApp.mount;
      unmount = mfeApp.unmount;
    } catch (error) {
      this.setState({ isLoading: false, isError: true, isRetrying: false });
      return;
    }

    this.setState({ mount, unmount, isLoading: false, isError: false, isRetrying: false });

    if (!this.componentRef.current) {
      return;
    }

    try {
      const response = mount(this.componentRef.current as Element, params);

      this.setState({ update: response.onParamsChange });
    } catch (error) {
      this.setState({ isLoading: false, isError: true, isRetrying: false });
    }
  }

  shouldComponentUpdate(nextProps: any): boolean {
    // Only call onParamsChange if props actually changed
    if (this.hasPropsChanged(this.props, nextProps)) {
      this.state.update?.(nextProps);
    }

    return true;
  }

  private hasPropsChanged(prevProps: any, nextProps: any): boolean {
    // Compare all relevant props for changes
    const prevKeys = Object.keys(prevProps);
    const nextKeys = Object.keys(nextProps);

    if (prevKeys.length !== nextKeys.length) {
      return true;
    }

    for (const key of prevKeys) {
      if (!this.deepEqual(prevProps[key], nextProps[key])) {
        return true;
      }
    }

    return false;
  }

  private deepEqual(a: any, b: any): boolean {
    if (a === b) return true;

    if (a == null || b == null) return a === b;

    if (typeof a !== typeof b) return false;

    if (typeof a === 'function' && typeof b === 'function') {
      return a.toString() === b.toString();
    }

    // For arrays
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (!this.deepEqual(a[i], b[i])) return false;
      }
      return true;
    }

    // For objects
    if (typeof a === 'object' && typeof b === 'object') {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);

      if (keysA.length !== keysB.length) return false;

      for (const key of keysA) {
        if (!keysB.includes(key)) return false;
        if (!this.deepEqual(a[key], b[key])) return false;
      }
      return true;
    }

    return false;
  }

  async componentDidMount() {
    const { exposedPath, name, entryPointUrl } = this.props;

    try {
      await loadScript(name, entryPointUrl);

      const container = window[name as any] as any;

      if (!container) {
        throw new Error(`Module '${name}' not found in window object`);
      }

      if (typeof container.init === 'function') {
        try {
          await container.init({});
        } catch {
          // Already initialized — safe to ignore
        }
      }

      const app = await container.get(exposedPath);

      this.afterLoad(app);
    } catch (error) {
      this.setState({ isLoading: false, isError: true, isRetrying: false });
    }
  }

  retryLoad = async () => {
    this.props.tracker?.track(
      'screen_error_displayed_refresh_clicked',
      this.getTrackingProperties(),
    );
    this.setState({ isRetrying: true, isError: false, isLoading: true });
    
    const { exposedPath, name, entryPointUrl } = this.props;

    // Limpiar el módulo cacheado para forzar recarga
    delete (window as any)[name];

    try {
      await loadScript(name, entryPointUrl, true, true);

      const container = window[name as any] as any;

      if (!container) {
        throw new Error(`Module '${name}' not found in window object`);
      }

      if (typeof container.init === 'function') {
        try {
          await container.init({});
        } catch {
          // Already initialized — safe to ignore
        }
      }

      const app = await container.get(exposedPath);
      this.afterLoad(app);
    } catch (error) {
      this.setState({ isLoading: false, isError: true, isRetrying: false });
    }
  };

  componentWillUnmount() {
    const { unmount } = this.state;
    const el = this.componentRef.current;
    if (unmount && el) unmount(el);
  }

  render() {
    const { isError, isLoading, isRetrying } = this.state;
    const {
      className,
      configLoading,
      errorMessage,
      loadingLogoSize,
      loadingLogoStyle,
      name,
      routeConfigLoading,
    } = this.props;

    return (
      <>
        {isLoading && name !== 'appProvider' && (
          <AllieLogoLoading
            config={configLoading ?? {
              autoHideTime: 10000,
              autoHide: true,
              safetyTime: 10000,
            }}
            routeConfig={routeConfigLoading}
            logoSize={loadingLogoSize}
            logoStyle={loadingLogoStyle}
            backgroundColor={isAppMobile ? 'neutral-10' : 'white'}
          />
        )}
        {isError && errorMessage && (
          <Suspense fallback={null}>
            <EmptyState
              type="error"
              title={errorMessage.TITLE}
              descriptionLine1={errorMessage.DESCRIPTION_LINE_1}
              descriptionLine2={errorMessage.DESCRIPTION_LINE_2}
              buttonText={errorMessage.BUTTON_TEXT}
              onRetry={this.retryLoad}
              isRetrying={isRetrying}
              size="large"
              background="grey"
            />
          </Suspense>
        )}

        <div
          className={className}
          style={{ display: isLoading ? 'none' : 'block' }}
          ref={(node) => {
            (this.componentRef as any).current = node;
          }}
        ></div>
      </>
    );
  }
}
